import type { ReactNode } from "react";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styles from "./DynamicFieldList.module.css";

type DynamicFieldListProps = {
  fields: { id: string; value: string }[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onMove: (from: number, to: number) => void;
  onChange: (index: number, value: string) => void;
  addLabel?: string;
  placeholder?: string;
  emptyMessage?: string;
};

function GripIcon() {
  return (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true">
      <circle cx="2" cy="2" r="1.5" />
      <circle cx="8" cy="2" r="1.5" />
      <circle cx="2" cy="8" r="1.5" />
      <circle cx="8" cy="8" r="1.5" />
      <circle cx="2" cy="14" r="1.5" />
      <circle cx="8" cy="14" r="1.5" />
    </svg>
  );
}

function SortableFieldItem({
  id,
  children,
}: {
  id: string;
  children: (dragHandleProps: {
    listeners: ReturnType<typeof useSortable>["listeners"];
    attributes: ReturnType<typeof useSortable>["attributes"];
  }) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? styles.dragging : undefined}>
      {children({ listeners, attributes })}
    </div>
  );
}

export function DynamicFieldList({
  fields,
  onAdd,
  onRemove,
  onMove,
  onChange,
  addLabel = "+ Add field",
  placeholder,
  emptyMessage,
}: DynamicFieldListProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const itemIds = fields.map(field => field.id);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = itemIds.indexOf(String(active.id));
      const newIndex = itemIds.indexOf(String(over.id));
      onMove(oldIndex, newIndex);
    }
  }

  return (
    <div>
      {fields.length === 0 && emptyMessage && <p className={styles.emptyMessage}>{emptyMessage}</p>}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <div className={styles.fieldList}>
            {fields.map((field, index) => (
              <SortableFieldItem key={field.id} id={field.id}>
                {({ listeners, attributes }) => (
                  <div className={styles.fieldRow}>
                    <button
                      type="button"
                      className={styles.dragHandle}
                      {...listeners}
                      {...attributes}
                      aria-label={`Reorder item ${index + 1}`}
                    >
                      <GripIcon />
                    </button>
                    <span className={styles.fieldNumber}>{index + 1}.</span>
                    <input
                      type="text"
                      value={field.value}
                      onChange={e => onChange(index, e.target.value)}
                      className={styles.input}
                      placeholder={placeholder}
                    />
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() => onRemove(index)}
                      aria-label={`Remove item ${index + 1}`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M2 4h12M5.33 4V2.67a1.33 1.33 0 011.34-1.34h2.66a1.33 1.33 0 011.34 1.34V4m2 0v9.33a1.33 1.33 0 01-1.34 1.34H4.67a1.33 1.33 0 01-1.34-1.34V4h9.34z"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </SortableFieldItem>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <button type="button" className={styles.addButton} onClick={onAdd}>
        {addLabel}
      </button>
    </div>
  );
}
